import { RedoPrescription, RedoReplaceState, ReissueAnswer } from "@types-runway/admin";
import { Order } from "@types-runway/case";

interface ReplacementOutcome {
  replacedOrder: Order | undefined;
  answer: ReissueAnswer;
}

const hasStatus = (answer: ReissueAnswer) => answer.status !== null && answer.status !== undefined;

// A replacement is already done when no Order was created: the Order replaced had a successor
// among the Orders shown, the Order answered was sent before (a new Order has no status), or the
// answer says it existed (REDOUI-R53).
export const isReplacementAlreadyDone = ({ replacedOrder, answer }: ReplacementOutcome) =>
  Boolean(replacedOrder?.supersededByOrderId) ||
  hasStatus(answer) ||
  answer.isExistingSuccessor === true;

interface AlreadyReplacedInput {
  orderId: string;
  answer: ReissueAnswer;
  prescriptions: RedoPrescription[];
  invoiceId: string;
  caseId: string;
}

type AlreadyReplacedOutcome = Omit<
  Extract<RedoReplaceState, { status: "alreadyReplaced" }>,
  "status"
>;

// What the banner of a replacement that already exists carries: both Orders and the creation of the
// one answered (REDOUI-R55), and what a re-send needs when that Order was never sent (REDOUI-R60).
export const describeAlreadyReplaced = ({
  orderId,
  answer,
  prescriptions,
  invoiceId,
  caseId,
}: AlreadyReplacedInput): AlreadyReplacedOutcome => {
  const outcome = { orderId, existingOrderId: answer.order_id, createdAt: answer.createdAt };
  if (hasStatus(answer)) return outcome;

  return { ...outcome, resend: { prescriptions, invoiceId, caseId, isPending: false } };
};
